import {FC, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {Box, Button, Stack} from '@mui/material';
import {useIntl} from 'react-intl';
import AMPModal from '../../common/AMPModal';
import {transactionsApi} from 'src/api/transactions';
import {ExportFileType} from 'src/types/transactions';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {CancelButton} from 'src/components/common/CancelButton';
import dayjs from 'dayjs';

interface CostItemsExportButtonProps {
    transactionId: number;
    showModal: boolean;
    onClose: () => void;
}

const modalContentSx = {
    padding: '32px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
};

const actionsWrapperSx = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 2,
    padding: 2,
    borderTop: '1px solid',
    borderColor: 'divider',
};

export const CostItemsExportButton: FC<CostItemsExportButtonProps> = ({transactionId, showModal, onClose}) => {
    // The control is the action: without the permission the server refuses it, so showing it
    // offers a button whose only outcome is an error.
    const canDoIt = useCan('costItems.export');
    const intl = useIntl();
    const [isLoading, setIsLoading] = useState(false);
    const [exportCostItems] = transactionsApi.useExportCostItemsMutation();

    const handleExport = async (fileType: ExportFileType) => {
        try {
            setIsLoading(true);

            const blob = await exportCostItems({
                transactionId,
                fileType,
            }).unwrap();

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const timestamp = dayjs().format('YYYY-MM-DD_HH-mm-ss');
            const fileName = `cost_items_transaction_${transactionId}_${timestamp}.${fileType.toLowerCase()}`;
            link.setAttribute('download', fileName);

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            NotifySuccess(intl.formatMessage({id: 'downloadSuccess'}) || 'Download started successfully');
            onClose();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data?.message || 'Export failed');
            } else {
                NotifyError('Export failed');
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (!canDoIt) {
        return null;
    }

    return (
        <>
            <AMPModal
                handleClose={onClose}
                open={showModal}
                title={intl.formatMessage({id: 'exportCosts'})}
                type="sm"
                fullWidth
                withoutPaddings
            >
                <Box sx={modalContentSx}>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button
                            variant="outlined"
                            onClick={() => handleExport(ExportFileType.CSV)}
                            disabled={isLoading}
                            sx={{minWidth: 100}}
                        >
                            CSV
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => handleExport(ExportFileType.PDF)}
                            disabled={isLoading}
                            sx={{minWidth: 100}}
                        >
                            PDF
                        </Button>
                    </Stack>
                </Box>
                <Box sx={actionsWrapperSx}>
                    <CancelButton
                        text="cancel"
                        handler={onClose}
                    />
                </Box>
            </AMPModal>
        </>
    );
};